import React, { useEffect, useRef, useState } from 'react'

import { Carousel } from 'antd'

import { NamedItemString, PlanPeriodDto, TotalSpentByCategory, getTotalSpentByCategory } from 'core'

import { Button, Card, Col, HistogramChart, Row, SimpleSelect, Skeleton } from 'common/components'
import { getMonths, isTodayAfter } from 'common/dateFormatters'
import { useQueryRequestV2 } from 'common/hooks'

type TotalSpendByCategoryProps = {
  planPeriod: PlanPeriodDto
}

function TotalSpendByCategory({ ...props }: TotalSpendByCategoryProps) {
  const { data: totalSpentByCategory, sendQuery: sendGetTotalSpentByCategory } =
    useQueryRequestV2<TotalSpentByCategory[]>()
  const [data, setData] = useState([])

  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [availableDateOptions, setAvailableDateOptions] = useState<NamedItemString[]>([])

  useEffect(() => {
    if (!props.planPeriod) return

    const months = getMonths(props.planPeriod.startDate, props.planPeriod.endDate).reverse()
    setAvailableDateOptions([{ id: null, name: 'All' }, ...months])
  }, [props.planPeriod])

  useEffect(() => {
    ;(async () => {
      if (props.planPeriod.id) {
        await sendGetTotalSpentByCategory(() => getTotalSpentByCategory(props.planPeriod.id, selectedDate))
      }
    })()
  }, [selectedDate, props.planPeriod])

  useEffect(() => {
    let array = []
    if (totalSpentByCategory) {
      for (let i = 0; i < totalSpentByCategory?.length; i++) {
        array[i] = { name: totalSpentByCategory[i].category.name, reimbursed: totalSpentByCategory[i].amount }
      }
      setData(array)
    }
  }, [totalSpentByCategory])

  const carouselRef = useRef(null)

  function handleSlideChange(action: 'previous' | 'next') {
    switch (action) {
      case 'previous':
        carouselRef?.current?.prev()
        break

      case 'next':
        carouselRef?.current?.next()
        break

      default:
        break
    }
  }

  return (
    <Card style={{ maxHeight: 442, minHeight: 442 }}>
      <Skeleton loading={false}>
        <div className="allowances-statistics-container">
          <Row justify="space-between">
            <Col span={12}>
              <p className="allowances-statistics_header mb-24">Total spent by category</p>
            </Col>
            <Col>
              <div style={{ minWidth: 100, alignContent: 'end' }}>
                <SimpleSelect
                  className={'t-month-drop-down-filter'}
                  value={selectedDate}
                  items={availableDateOptions}
                  onChange={(value) => setSelectedDate(value)}
                />
              </div>
            </Col>
          </Row>

          {totalSpentByCategory && totalSpentByCategory?.length === 0 ? (
            <div className="allowances-statistics_card mb-32">
              <div className="text-center" style={{ margin: 'auto' }}>
                <div className="allowances-statistics_card-title">
                  <span className="allowances-statistics_card-zero-state-text semi-bold">No data available yet...</span>
                </div>

                <span className="allowances-statistics_card-zero-state-paragraph">
                  {isTodayAfter(props.planPeriod?.startDate)
                    ? 'No reimbursements for this period'
                    : 'This data will be available after your benefit starts.'}
                </span>
              </div>
            </div>
          ) : (
            <Carousel ref={carouselRef} dots={false}>
              <Row>
                <Col span={24}>
                  <HistogramChart
                    data={data}
                    xField="name"
                    yField="reimbursed"
                    xAxis={{
                      label: {
                        autoHide: true,
                        autoRotate: false,
                      },
                    }}
                    meta={{
                      reimbursed: {
                        alias: 'Reimbursed',
                        formatter: (value: number) => `$${value}`,
                      },
                    }}
                    label={{
                      position: 'middle',
                      style: {
                        fill: '#FFFFFF',
                        opacity: 0.6,
                      },
                    }}
                    style={{ height: 250 }}
                    columnStyle={{
                      fill: '#11D175',
                    }}
                  />
                </Col>
              </Row>
            </Carousel>
          )}

          {totalSpentByCategory?.length > 0 && (
            <Row className={'category-spending'}>
              <Col className="category-spending__footer">
                <Row className="category-spending__footer-controls">
                  <Col span={12}>
                    <Button
                      className="mr-16"
                      type="default"
                      icon="fa fa-arrow-left"
                      onClick={() => {
                        handleSlideChange('previous')
                      }}
                    />
                  </Col>
                  <Col span={12}>
                    <Button
                      type="default"
                      className=""
                      icon="fa fa-arrow-right"
                      onClick={() => {
                        handleSlideChange('next')
                      }}
                    />
                  </Col>
                </Row>
                <p className="category-spending__footer-indicator">
                  Showing {data.length} of {totalSpentByCategory?.length} total categories
                </p>
              </Col>
            </Row>
          )}
        </div>
      </Skeleton>
    </Card>
  )
}

export default TotalSpendByCategory
